import { isSome } from '@mbfab/shared'

import type { ColumnConfig } from './svg/main-row'
import { columnConfigSchema, columnTypeLabel } from './svg/main-row'

//
// 発注照合「鋼材ミルシート集計表」専用の列構成。
// v1 apps/frontend/client/src/components/orderMatching/SteelOrderMillsheetPrintForm.tsx の
// 列定義部分（要件定義書 07/06 版 3.2）を移植したもの。
// 汎用ミルシート印刷（一覧表）とは列構成・既定値が異なるため、main-row.tsx 側の汎用定数は使わずここで独自に持つ。
//

// 表示項目（07/06 版 3.2 の区分どおり）。ページ番号は常に先頭固定
export const REORDERABLE_COLUMN_TYPES: Array<ColumnConfig['type']> = [
  'sectionType', // 形状
  'steelType', // 規格
  'manufacturerName', // 製造会社名
  'tradingCompany', // 商社名
  'endorsementCompany', // 裏書会社名
  'customer', // 需要家名
  'referenceNumber', // 参照番号
]
export const FIXED_ORDER_COLUMN_TYPES: Array<ColumnConfig['type']> = [
  'size', // 寸法
  'length', // 長さ
  'quantity', // 数量
  'weight', // 重量
  'heatNumber', // 製鋼番号
]

export const EXPORT_REORDERABLE_COLUMN_TYPES: Array<ColumnConfig['type']> = [
  'referenceNumber',
  'no',
  'manufacturerName',
  'steelType',
  'sectionType',
  'tradingCompany',
  'endorsementCompany',
  'customer',
  'supplierName',
  'constructionArea',
  'member',
  'buildingPart',
  'orderNumber',
  'innerNo',
]
export const EXPORT_FIXED_ORDER_COLUMN_TYPES: Array<ColumnConfig['type']> = [
  'size',
  'thicknessDiameter',
  'length',
  'quantity',
  'weight',
  'heatNumber',
  'individualNumber',
  'memo',
]
const DEFAULT_ACTIVE_EXPORT_COLUMN_TYPES: Array<ColumnConfig['type']> = [
  'referenceNumber',
  'no',
  'manufacturerName',
  'steelType',
  'sectionType',
  'tradingCompany',
  'endorsementCompany',
  'customer',
  // 発注データ由来の列は既定 ON にする方針（2026/08/10 FB）に合わせる
  'supplierName',
  'size',
  'thicknessDiameter',
  'length',
  'quantity',
  'weight',
  'heatNumber',
]

// 発注照合印刷で選べる列（並び替え可能・順番固定の両方）。
export const SELECTABLE_COLUMN_TYPES: Array<ColumnConfig['type']> = [
  ...REORDERABLE_COLUMN_TYPES,
  ...FIXED_ORDER_COLUMN_TYPES,
]

// 既定でONにする表示項目。全項目を出すと列幅が潰れて集計表が読めなくなるため、重要な項目だけを既定ONにする
// （v1 と同じ既定値。2026/08/04 FB）
export const DEFAULT_ACTIVE_COLUMN_TYPES: Array<ColumnConfig['type']> = [
  'manufacturerName',
  'steelType',
  'size',
  'heatNumber',
]

// 集計表の項目名。既存の共通ラベル（columnTypeLabel）に対し、この帳票でだけ「商社」→「商社名」とする
// （v1 の printColumnTypeLabel と同じ）
export const printColumnTypeLabel: Record<ColumnConfig['type'], string> = {
  ...columnTypeLabel,
  tradingCompany: '商社名',
}

// 集計表（A4 1枚に横並びで収める帳票）でレイアウト崩れが出始める列数（ページ番号込み）。
// v1 と同じ閾値（2026/08/10 FB「6列以上でアラートを出してほしい」）
export const SUMMARY_COLUMN_COUNT_WARNING_THRESHOLD = 6

// 1ページ分の明細行数（v1 の集計表と同じ）
export const SLICE_NUMBER = 18

// active な列だけを出力順（ページ番号 → 入れ替え可能な列 → 順番固定の列）に並べ直す（v1 と同じロジック）
export function fixColumnOrderFor(columnConfigs: ColumnConfig[]): ColumnConfig[] {
  return [
    { type: 'pageNumber', active: true },
    ...columnConfigs.filter((x) => x.active && REORDERABLE_COLUMN_TYPES.includes(x.type)),
    ...FIXED_ORDER_COLUMN_TYPES.flatMap((type) => {
      const config = columnConfigs.find((x) => x.type === type)
      return isSome(config) && config.active ? [config] : []
    }),
  ]
}

export function initialColumnConfigs(): ColumnConfig[] {
  return SELECTABLE_COLUMN_TYPES.map((type) => ({ type, active: DEFAULT_ACTIVE_COLUMN_TYPES.includes(type) }))
}

export function initialExportColumnConfigs(): ColumnConfig[] {
  return [...EXPORT_REORDERABLE_COLUMN_TYPES, ...EXPORT_FIXED_ORDER_COLUMN_TYPES].map((type) => ({
    type,
    active: DEFAULT_ACTIVE_EXPORT_COLUMN_TYPES.includes(type),
  }))
}

export function fixExportColumnOrderFor(columnConfigs: ColumnConfig[]): ColumnConfig[] {
  return [
    { type: 'pageNumber', active: true },
    ...columnConfigs.filter((x) => x.active && EXPORT_REORDERABLE_COLUMN_TYPES.includes(x.type)),
    ...EXPORT_FIXED_ORDER_COLUMN_TYPES.flatMap((type) => {
      const config = columnConfigs.find((x) => x.type === type)
      return isSome(config) && config.active ? [config] : []
    }),
  ]
}

// 保存済みの列設定に、その後追加された列（例: 2026/08 のサプライヤー名）を既定の active 状態で末尾に補完する。
// 保存側の並び順・ON/OFF はそのまま尊重する（利用者が並べ替えた結果を壊さないため）
export function withMissingColumnConfigs(saved: ColumnConfig[], defaults: ColumnConfig[]): ColumnConfig[] {
  const savedTypes = new Set(saved.map((config) => config.type))
  const missing = defaults.filter((config) => !savedTypes.has(config.type))
  return [...saved, ...missing]
}

// useLocalStorageState の復元用 schema。zod の safeParse でそのまま配列を返すだけだと、
// 新列を追加しても既に保存済みの利用者には表示されない（ExportTab は保存配列を filter して描画するため）。
// transform で補完まで済ませてから使う
export const restoredExportColumnConfigsSchema = columnConfigSchema
  .array()
  .transform((saved) => withMissingColumnConfigs(saved, initialExportColumnConfigs()))
